/**
 * Cross-Domain Authentication Utility
 * Handles authentication token transfer when switching between different course domains
 * (e.g., from stagingstudent.acadimyst.com to stagingielts.acadimyst.com)
 */

/**
 * Builds a URL with authentication token for cross-domain navigation
 * @param {string} targetUrl - The target URL to navigate to
 * @param {string} currentDomain - The current domain (optional, defaults to window.location.hostname)
 * @returns {string} URL with token appended if cross-domain, otherwise original URL
 */
export const buildCrossDomainUrl = (targetUrl, currentDomain = null) => {
  try {
    const domain = currentDomain || window.location.hostname;
    const url = new URL(targetUrl);
    
    // Check if we're switching domains
    if (url.hostname !== domain) {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (token) {
        // Append token as URL parameter for cross-domain transfer
        url.searchParams.append('token', token);
        console.log('[Cross-Domain Auth] Token added to URL for domain switch:', {
          from: domain,
          to: url.hostname
        });
      } else {
        console.warn('[Cross-Domain Auth] No token found in localStorage');
      }
    }
    
    return url.toString();
  } catch (error) {
    console.error('[Cross-Domain Auth] Error building URL:', error);
    return targetUrl;
  }
};

/**
 * Extracts and stores authentication token from URL parameters
 * Should be called during app initialization
 * @returns {boolean} True if token was found and stored, false otherwise
 */
export const extractTokenFromUrl = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      console.log('[Cross-Domain Auth] Token received from URL - storing in localStorage');
      localStorage.setItem('token', token);
      
      // Remove token from URL for security
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('[Cross-Domain Auth] Error extracting token:', error);
    return false;
  }
};

/**
 * Checks if two URLs are on different domains
 * @param {string} url1 - First URL
 * @param {string} url2 - Second URL (optional, defaults to current location)
 * @returns {boolean} True if domains are different
 */
export const isDifferentDomain = (url1, url2 = null) => {
  try {
    const domain1 = new URL(url1).hostname;
    const domain2 = url2 ? new URL(url2).hostname : window.location.hostname;
    return domain1 !== domain2;
  } catch (error) {
    console.error('[Cross-Domain Auth] Error comparing domains:', error);
    return false;
  }
};
